import { useCallback, useState } from 'react';
import type { DragEvent } from 'react';

// Parent-owned, kind-checked drag system. Lifted from v5's design and
// flagged as load-bearing in BRIEF §11: "Don't move drag state back
// into individual table components."
//
// One source of drag state lives at App level. The hook returns a
// system object with:
//   • state predicates (isDragging, rowDropPosition, …) for visuals
//   • handler factories (rowHandlers, opSectionHandlers, …) the
//     components attach to their elements
// Income rows can only drop on income drop zones; expense rows on
// expense drop zones. Cross-kind drops are blocked at handler level
// (drag.kind !== zone.kind → handler returns early).

export type DragKind = 'income' | 'expense';

type DragState = { id: string; kind: DragKind } | { id: null; kind: null };

type DropTarget =
  | { kind: 'row'; tableKind: DragKind; id: string; position: 'before' | 'after' }
  | { kind: 'op-section'; tableKind: DragKind; categoryId: string }
  | { kind: 'project'; tableKind: DragKind; projectId: string }
  | null;

export type MoveTarget =
  | { type: 'before' | 'after'; id: string }
  | { type: 'into-section'; categoryId: string }
  | { type: 'into-project'; projectId: string };

export type RowDragHandlers = {
  draggable: true;
  onDragStart: (e: DragEvent<HTMLTableRowElement>) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent<HTMLTableRowElement>) => void;
  onDrop: (e: DragEvent<HTMLTableRowElement>) => void;
};

export type DropZoneHandlers = {
  onDragOver: (e: DragEvent<HTMLTableRowElement>) => void;
  onDrop: (e: DragEvent<HTMLTableRowElement>) => void;
};

export type DragSystem = {
  drag: DragState;
  anyDragging: boolean;
  isDragging: (id: string) => boolean;
  isDraggingKind: (kind: DragKind) => boolean;
  rowDropPosition: (id: string) => 'before' | 'after' | null;
  isOpSectionTarget: (kind: DragKind, categoryId: string) => boolean;
  isProjectTarget: (kind: DragKind, projectId: string) => boolean;
  rowHandlers: (kind: DragKind, id: string) => RowDragHandlers;
  opSectionHandlers: (kind: DragKind, categoryId: string) => DropZoneHandlers;
  projectHandlers: (kind: DragKind, projectId: string) => DropZoneHandlers;
};

export function useDragSystem(
  onMove: (kind: DragKind, draggedId: string, target: MoveTarget) => void,
): DragSystem {
  const [drag, setDrag] = useState<DragState>({ id: null, kind: null });
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);

  const reset = useCallback(() => {
    setDrag({ id: null, kind: null });
    setDropTarget(null);
  }, []);

  return {
    drag,
    anyDragging: drag.id !== null,
    isDragging: (id) => drag.id === id,
    isDraggingKind: (kind) => drag.kind === kind,
    rowDropPosition: (id) =>
      dropTarget && dropTarget.kind === 'row' && dropTarget.id === id ? dropTarget.position : null,
    isOpSectionTarget: (kind, categoryId) =>
      !!(
        dropTarget &&
        dropTarget.kind === 'op-section' &&
        dropTarget.tableKind === kind &&
        dropTarget.categoryId === categoryId
      ),
    isProjectTarget: (kind, projectId) =>
      !!(
        dropTarget &&
        dropTarget.kind === 'project' &&
        dropTarget.tableKind === kind &&
        dropTarget.projectId === projectId
      ),

    rowHandlers: (kind, id) => ({
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
        setDrag({ id, kind });
      },
      onDragEnd: reset,
      onDragOver: (e) => {
        if (drag.id === null || drag.kind !== kind || drag.id === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const position: 'before' | 'after' =
          e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
        setDropTarget((prev) =>
          prev && prev.kind === 'row' && prev.id === id && prev.position === position
            ? prev
            : { kind: 'row', tableKind: kind, id, position },
        );
      },
      onDrop: (e) => {
        if (drag.id === null || drag.kind !== kind || drag.id === id) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const position: 'before' | 'after' =
          e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
        onMove(kind, drag.id, { type: position, id });
        reset();
      },
    }),

    opSectionHandlers: (kind, categoryId) => ({
      onDragOver: (e) => {
        if (drag.id === null || drag.kind !== kind) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTarget((prev) =>
          prev &&
          prev.kind === 'op-section' &&
          prev.tableKind === kind &&
          prev.categoryId === categoryId
            ? prev
            : { kind: 'op-section', tableKind: kind, categoryId },
        );
      },
      onDrop: (e) => {
        if (drag.id === null || drag.kind !== kind) return;
        e.preventDefault();
        onMove(kind, drag.id, { type: 'into-section', categoryId });
        reset();
      },
    }),

    projectHandlers: (kind, projectId) => ({
      onDragOver: (e) => {
        if (drag.id === null || drag.kind !== kind) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTarget((prev) =>
          prev && prev.kind === 'project' && prev.tableKind === kind && prev.projectId === projectId
            ? prev
            : { kind: 'project', tableKind: kind, projectId },
        );
      },
      onDrop: (e) => {
        if (drag.id === null || drag.kind !== kind) return;
        e.preventDefault();
        onMove(kind, drag.id, { type: 'into-project', projectId });
        reset();
      },
    }),
  };
}
